import React from "react";
import { Badge, Table, Tag } from "antd";
import { DashboardOutlined } from "@ant-design/icons";

const LineGraph = ({ data = [], color = "#3b82f6" }) => {
  if (!data.length) return null;

  const max = Math.max(...data);
  const min = Math.min(...data);

  const points = data
    .map((v, i) => {
      const x = (i / (data.length - 1)) * 100;
      const y = 90 - ((v - min) / (max - min || 1)) * 70;
      return { x, y, v };
    });

  const path = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`)
    .join(" ");

  const areaPath = `
    ${path}
    L 100 90
    L 0 90
    Z
  `;

  return (
    <svg viewBox="0 0 100 100" className="w-full h-28">
      {/* Grid */}
      {[20, 40, 60, 80].map(y => (
        <line
          key={y}
          x1="0"
          y1={y}
          x2="100"
          y2={y}
          stroke="#f1f5f9"
          strokeWidth="0.5"
        />
      ))}

      {/* Gradient */}
      <defs>
        <linearGradient id={`lineGradient-${color}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0.05" />
        </linearGradient>
      </defs>

      {/* Area */}
      <path d={areaPath} fill={`url(#lineGradient-${color})`} />

      {/* Line */}
      <path
        d={path}
        fill="none"
        stroke={color}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Points */}
      {points.map((p, i) => (
        <circle
          key={i}
          cx={p.x}
          cy={p.y}
          r="2.5"
          fill="white"
          stroke={color}
          strokeWidth="2"
        />
      ))}
    </svg>
  );
};

const GateEntryExitTable = () => {

  const columns = [
    {
      title: "Staff ID",
      dataIndex: "staffId",
      key: "staffId"
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name"
    },
    {
      title: "Entry Time",
      dataIndex: "entry",
      key: "entry"
    },
    {
      title: "Exit Time",
      dataIndex: "exit",
      key: "exit",
      render: (exit) => exit || "-"
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) =>
        status === "Inside" ? (
          <Tag color="green">Inside</Tag>
        ) : (
          <Tag color="blue">Exited</Tag>
        )
    }
  ];

  const data = [
    {
      key: 1,
      staffId: "ST101",
      name: "John",
      entry: "08:45",
      exit: "16:10",
      status: "Exited"
    },
    {
      key: 2,
      staffId: "ST102",
      name: "Mary",
      entry: "09:05",
      exit: null,
      status: "Inside"
    },
    {
      key: 3,
      staffId: "ST103",
      name: "Alex",
      entry: "08:50",
      exit: "15:55",
      status: "Exited"
    }
  ];

  return (
    <Table
      columns={columns}
      dataSource={data}
      size="small"
      pagination={false}
    />
  );
};

export const widgetPreviewRegistry = {

  /* ===================== QSR / CANTEEN ===================== */

  "table-occupancy": () => (
    <Kpi
      value="18 / 25"
      label="Table Occupancy"
      trend="72% occupied"
      color="orange"
    />
  ),

  "occupied-table-count": () => (
    <Kpi
      value="18"
      label="Occupied Tables"
      trend="High usage"
      color="red"
    />
  ),

  "available-table-count": () => (
    <Kpi
      value="7"
      label="Available Tables"
      trend="Free seats"
      color="green"
    />
  ),

  "mobile-usage-alerts": () => (
    <Kpi
      value="3"
      label="Mobile Usage Alerts"
      trend="Staff distraction"
      color="purple"
    />
  ),

  "table-usage-report": () => (
    <Graph data={[10,15,20,18,22]} />
  ),

  "mobile-usage-report": () => (
    <Graph data={[2,4,3,5,6]} />
  ),

  "occupancy-history": () => (
    <Graph data={[12,18,20,22,19]} />
  ),

  /* ---- Parking Occupancy Monitoring ---- */

  "parking-utilisation": () => (
    <Kpi
      value="68%"
      label="Parking Utilisation"
      trend="+5% today"
      color="blue"
    />
  ),

  "occupancy-score": () => (
    <Kpi
      value="82"
      label="Occupancy Score"
      trend="Efficient usage"
      color="green"
    />
  ),

  "occupancy-table": () => (
    <AlertList />
  ),

  /* ---- Vehicle Counting ---- */

  "vehicle-entry-count": () => (
    <Kpi
      value="124"
      label="Vehicle Entries"
      trend="+10 today"
      color="blue"
    />
  ),

  "vehicle-exit-count": () => (
    <Kpi
      value="118"
      label="Vehicle Exits"
      trend="Balanced flow"
      color="green"
    />
  ),

  "current-vehicle-count": () => (
    <Kpi
      value="46"
      label="Current Vehicles"
      trend="Inside parking"
      color="indigo"
    />
  ),

  "traffic-load": () => (
    <Kpi
      value="High"
      label="Traffic Load"
      trend="Peak hour"
      color="red"
    />
  ),

  "vehicle-log": () => (
    <AlertList />
  ),

  /* ---- Illegal Parking Detection ---- */

  "illegal-parking-count": () => (
    <Kpi
      value="9"
      label="Illegal Parking"
      trend="+2 today"
      color="red"
    />
  ),

  "violation-zones": () => (
    <Graph data={[5, 8, 3, 6]} />
  ),

  "violation-log": () => (
    <AlertList />
  ),

  /* ---- Parking Duration Tracking ---- */

  "avg-parking-duration": () => (
    <Kpi
      value="1h 20m"
      label="Avg Duration"
      trend="+10m"
      color="blue"
    />
  ),

  "overstay-vehicles": () => (
    <Kpi
      value="6"
      label="Overstay Vehicles"
      trend="Needs attention"
      color="orange"
    />
  ),

  "duration-distribution": () => (
    <Graph data={[10, 20, 35, 25, 15]} />
  ),

  "duration-trend": () => (
    <Graph data={[30, 40, 45, 50, 60]} />
  ),

  "overstay-report": () => (
    <AlertList />
  ),

  /* ===================== CORE KPI ===================== */

  "live-occupancy": () => (
    <Kpi
      value="124"
      label="People inside"
      trend="+8 since last hour"
      color="blue"
    />
  ),

  "live-count": () => (
    <Kpi
      value="356"
      label="Live Count"
      trend="+12 today"
      color="indigo"
    />
  ),

  "total-alerts": () => (
    <Kpi
      value="12"
      label="Active Alerts"
      trend="3 high severity"
      color="red"
    />
  ),

  "violation-count": () => (
    <Kpi
      value="9"
      label="Violations"
      trend="+2 today"
      color="orange"
    />
  ),

  "status-indicator": () => (
    <StatusCard status="Operational" color="green" />
  ),

  "active-cameras": () => (
    <Kpi
      value="18/20"
      label="Cameras Online"
      trend="90% uptime"
      color="green"
    />
  ),

  /* ===================== SAFETY / ALERT ===================== */

  "recent-alerts": () => (
    <AlertList />
  ),

  "alert-list": () => (
    <AlertList />
  ),

  "unsafe-proximity-count": () => (
    <Kpi
      value="4"
      label="Unsafe Proximity"
      trend="High Risk"
      color="red"
    />
  ),

  /* ===================== RETAIL / PARKING ===================== */

  "mobile-usage": () => (
    <Kpi
      value="63%"
      label="Mobile Usage"
      trend="Restricted Zone"
      color="purple"
    />
  ),

  "queue-length": () => (
    <Kpi
      value="7"
      label="Queue Length"
      trend="Long queue"
      color="amber"
    />
  ),

  "avg-dwell-time": () => (
    <Kpi
      value="14m"
      label="Avg Dwell Time"
      trend="+2m"
      color="teal"
    />
  ),

  "entry-exit-count": () => (
    <Kpi
      value="124 / 118"
      label="Entry / Exit"
      trend="Balanced"
      color="blue"
    />
  ),

  /* ===================== ANALYTICS ===================== */

  "trend-graph": () => (
    <Graph data={[10, 20, 35, 40, 55, 70]} />
  ),

  "people-count-analytics": () => (
    <Graph data={[80, 95, 110, 130, 150]} />
  ),

  "crowd-levels": () => (
    <Graph data={[20, 30, 45, 60, 72]} />
  ),

  "parking-duration": () => (
    <Graph data={[15, 22, 30, 40, 55]} />
  ),

  "zone-breakdown": () => (
    <Graph data={[40, 25, 20, 15]} />
  ),

  "staff-utilization": () => (
    <Kpi value="78%" label="Staff Utilization" trend="+5%" color="indigo" />
  ),

  "productivity-score": () => (
    <Kpi value="82" label="Productivity Score" trend="Good" color="green" />
  ),

  "compliance-score": () => (
    <Kpi value="91%" label="Compliance Score" trend="+3%" color="green" />
  ),

  "risk-score": () => (
    <Kpi value="Medium" label="Risk Level" trend="Monitor" color="orange" />
  ),

  "peak-hour": () => (
    <Kpi value="7-8 PM" label="Peak Hour" trend="High traffic" color="purple" />
  ),

  "queue-abandon-rate": () => (
    <Kpi value="12%" label="Queue Abandon Rate" trend="-3%" color="red" />
  ),

  "machine-health-score": () => (
    <Kpi value="87%" label="Machine Health" trend="Stable" color="green" />
  ),

  "fatigue-score": () => (
    <Kpi value="High" label="Worker Fatigue" trend="Take break" color="red" />
  ),

  /* TABLES */

  "incident-table": () => (
    <AlertList />
  ),

  "visitor-log-table": () => (
    <AlertList />
  ),

  "staff-performance-table": () => (
    <AlertList />
  ),

  /* AI INSIGHTS */

  "ai-insights": () => (
    <div className="text-sm p-2">
      Queue increased 22% between 7-8 PM.
    </div>
  ),

  "optimization-suggestions": () => (
    <div className="text-sm p-2">
      Suggest adding one extra counter during peak hours.
    </div>
  ),

  /* EXTRA GRAPHS */

  "shift-performance": () => (
    <Graph data={[30,40,55,70]} />
  ),

  "hourly-activity-graph": () => (
    <Graph data={[10,25,35,50,80]} />
  ),

  "queue-time-distribution": () => (
    <Graph data={[5,10,15,25,30]} />
  ),

  "incident-distribution": () => (
    <Graph data={[20,10,5,15]} />
  ),

  "anomaly-trend": () => (
    <Graph data={[2,3,1,4,2]} />
  ),

  /* ===================== EXTRA KPI ===================== */

  "anomaly-rate": () => (
    <Kpi value="5%" label="Anomaly Rate" trend="-2%" color="red" />
  ),

  "visitor-count": () => (
    <Kpi value="34" label="Visitors Today" trend="+4" color="blue" />
  ),

  "asset-utilization": () => (
    <Kpi value="76%" label="Asset Utilization" trend="+3%" color="indigo" />
  ),

  "throughput-rate": () => (
    <Kpi value="120/hr" label="Throughput Rate" trend="Stable" color="green" />
  ),

  "idle-vs-active": () => (
    <Kpi value="80% Active" label="Idle vs Active" trend="Good" color="green" />
  ),

  "available-slots": () => (
    <Kpi value="45" label="Available Slots" trend="Parking free" color="green" />
  ),

  "occupied-slots": () => (
    <Kpi value="78" label="Occupied Slots" trend="Busy" color="orange" />
  ),

  /* ===================== EXTRA GRAPHS ===================== */

  "attendance-trend": () => (
    <Graph data={[50,60,65,70,80]} />
  ),

  "compliance-trend": () => (
    <Graph data={[85,88,90,92,95]} />
  ),

  "heatmap-visualization": () => (
    <Graph data={[10,30,50,70,90]} />
  ),

  "zone-comparison-chart": () => (
    <Graph data={[40,60,30,70]} />
  ),

  "customer-flow-sankey": () => (
    <Graph data={[15,35,60,80]} />
  ),

  "heatmap-timeline": () => (
    <Graph data={[20,25,30,45,60]} />
  ),

  /* ===================== TABLES ===================== */

  "vehicle-log-table": () => (
    <AlertList />
  ),

  "machine-health-table": () => (
    <AlertList />
  ),

  "asset-log-table": () => (
    <AlertList />
  ),

  "shelf-status-table": () => (
    <AlertList />
  ),

  /* ===================== SYSTEM ===================== */

  "maintenance-status": () => (
    <Kpi value="OK" label="Maintenance Status" trend="No issues" color="green" />
  ),
  /* ===================== EDUCATION ===================== */

"staff-entry-count": () => (
  <Kpi
    value="28"
    label="Staff Entered"
    trend="+3 today"
    color="blue"
  />
),

"staff-exit-count": () => (
  <Kpi
    value="25"
    label="Staff Exited"
    trend="3 inside campus"
    color="green"
  />
),

"staff-inside-count": () => (
  <Kpi
    value="3"
    label="Staff Inside"
    trend="Currently active"
    color="indigo"
  />
),

"staff-attendance-trend": () => (
  <Graph data={[12,18,20,25,28]} />
),

"gate-entry-exit-log": () => (
  <GateEntryExitTable />
),


"late-staff-count": () => (
  <Kpi value="4" label="Late Staff Today" trend="After 9:00 AM" color="orange" />
),

"early-exit-count": () => (
  <Kpi value="2" label="Early Exit" trend="Before shift end" color="red" />
),

"most-active-staff": () => (
  <Kpi value="John" label="Most Active Staff" trend="Longest presence" color="green" />
),

"least-active-staff": () => (
  <Kpi value="Alex" label="Least Active Staff" trend="Lowest hours" color="purple" />
),

"hourly-entry-trend": () => (
  <Graph data={[2,5,10,8,3]} />
),

"hourly-exit-trend": () => (
  <Graph data={[1,3,7,9,5]} />
),

"weekly-attendance-trend": () => (
  <Graph data={[20,22,25,23,26]} />
),

"staff-working-hours-chart": () => (
  <Graph data={[7,8,6,8,7]} />
),

"staff-entry-exit-summary": () => (
  <GateEntryExitTable />
),

"staff-working-hours-table": () => (
  <GateEntryExitTable />
),

"late-staff-table": () => (
  <GateEntryExitTable />
),
  };

/* ===================== REUSABLE PREVIEW COMPONENTS ===================== */

const Kpi = ({ value, label, trend, color }) => (
  <div className="flex flex-col items-center justify-center h-full">
    <div className={`text-5xl font-bold text-${color}-600`}>
      {value}
    </div>
    <div className="text-sm text-gray-600 mt-1 font-medium">
      {label}
    </div>
    {trend && (
      <div className="text-xs text-gray-500 mt-2">
        {trend}
      </div>
    )}
  </div>
);

const StatusCard = ({ status, color }) => (
  <div className="flex flex-col items-center justify-center h-full">
    <Badge status="success" />
    <div className={`mt-2 font-semibold text-${color}-600`}>
      {status}
    </div>
  </div>
);

const AlertList = () => (
  <ul className="text-sm space-y-2">
    <li className="flex justify-between p-2 bg-red-50 rounded">
      <span>Crowd exceeded</span>
      <Badge status="error" />
    </li>
    <li className="flex justify-between p-2 bg-yellow-50 rounded">
      <span>Camera offline</span>
      <Badge status="warning" />
    </li>
    <li className="flex justify-between p-2 bg-green-50 rounded">
      <span>Unauthorized access</span>
      <Badge status="success" />
    </li>
  </ul>
);

const Graph = ({ data }) => (
  <div className="pt-2">
    <LineGraph data={data} />
  </div>
);
